import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Button } from 'primereact/button'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { forgotPasswordWithCode, getTokenForAssociationCode, loginWithUsernamePasswordV2 } from '../../store/auth/authActions'
import {
  clearAuth,
  clearAuthErrors,
  clearForgotPasswordStatus,
  selectAuthenticatedUser,
  selectAuthErrors,
  selectAuthMessage,
  selectForgotPasswordStatus,
  selectLoginRedirect,
  selectLoginStatus,
  setAuthErrors,
  setLoginRedirectUrl,
} from '../../store/auth/authSlice'
import { useLoginToAcademyMutation } from '../../store/academy/academy'
import { useLoginToDocsMutation } from '../../store/docs/docs'
import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Password } from 'primereact/password'
import { classNames } from 'primereact/utils'
import { SocialButtons } from './SocialButtons'
import { AuthSeparator } from './Separator'
import MailLogo from '../../assets/images/auth/mail-logo.svg'
import { EmailInput } from './EmailInput'
import { Redirect } from './Redirect'
import { useNavigateToOnboarding } from '../onboarding/hooks'
import './Auth.scss'
import { LOGIN_WAIT_LIST_MESSAGE, WAIT_LIST_MESSAGE } from './const'
import { useCookies } from 'react-cookie'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { CaptchaNote } from './CaptchaNote'
import { readToken, setStayLoggedIn, stayLoggedIn, Token } from '../../store/token/token'
import { Form } from 'react-bootstrap'
import { useCompanySso } from '../../common/companySso'
import { AuthErrorToast } from './AuthErrorToast'
import { ERROR_INVITE_CODE_EXPIRED, ERROR_INVITE_CODE_EXPIRED_AND_RESENT, FULL_PAGE_ERROR_CODES } from '../../common/errorCodes'
import { LoginErrorPanel } from './LoginErrorPanel'
import { SignupHeader } from './headers/SignupHeader'
import { decodeState, isSafeRedirect } from './utils'

const DASHBOARD_URL = process.env.REACT_APP_DASHBOARD_URL!
const CONSOLE_URL = process.env.REACT_APP_CONSOLE_URL!

const LoginViewV2: React.FC = () => {
  const [cookies] = useCookies(['preferredAppVersion'])
  const [forgotPassword, setForgotPassword] = useState<boolean>(false)
  const [searchParams] = useSearchParams()
  const [loginToAcademy] = useLoginToAcademyMutation()
  const [loginToDocs] = useLoginToDocsMutation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const navigateToOnboarding = useNavigateToOnboarding()
  const state = searchParams.get('state')
  const decodedState = state ? decodeState(state) : null
  const isAcademy = (decodedState?.sourceClient || searchParams.get('sourceClient')) === 'academy'
  const isDocs = (decodedState?.sourceClient || searchParams.get('sourceClient')) === 'docs'
  const authErrors = useAppSelector(selectAuthErrors)
  const authMessage = useAppSelector(selectAuthMessage)
  const loginStatus = useAppSelector(selectLoginStatus)
  const authenticatedUser = useAppSelector(selectAuthenticatedUser)
  const loginRedirect = useAppSelector(selectLoginRedirect)
  const { handlePreferredAuthMethod, handlePreferredAuthMethodRedirect } = useCompanySso()
  const isRedirectingToAcademy = useRef(false)
  const isRedirectingToDocs = useRef(false)

  useEffect(() => {
    dispatch(clearAuthErrors())
  }, [dispatch])

  useEffect(() => {
    if (isRedirectingToAcademy.current || isRedirectingToDocs.current) {
      return
    }
    if (authenticatedUser) {
      handlePreferredAuthMethod()
      const token = Token.fromSerialized(authenticatedUser.token)
      if (token.tokenUse === 'mfa') {
        const mfaUrl = `${DASHBOARD_URL}/mfa`
        navigateToOnboarding(mfaUrl)
      } else if (token.tokenUse === 'loginMethodChangeRequested') {
        const signUpRequestedUrl = `${DASHBOARD_URL}/sign-up-requested`
        navigateToOnboarding(signUpRequestedUrl)
      } else {
        const runAsync = async () => {
          if (isAcademy) {
            try {
              isRedirectingToAcademy.current = true
              const response = await loginToAcademy(null).unwrap()
              if (response?.data?.loginUrl) {
                window.location.href = response.data.loginUrl
                return
              }
            } catch (e) {
              console.error(e)
            }
          }
          if (isDocs) {
            try {
              isRedirectingToDocs.current = true
              const redirect = decodedState?.redirect || searchParams.get('redirect') || '/'
              const response = await loginToDocs({ redirect }).unwrap()
              if (response?.data?.redirectUrl) {
                window.location.href = response.data.redirectUrl
                return
              }
            } catch (e) {
              console.error(e)
            }
          }
          const preferredAppVersion = cookies['preferredAppVersion'] ? cookies['preferredAppVersion'] : 'dashboard'
          const preferredAppUrl = preferredAppVersion === 'console' ? CONSOLE_URL : DASHBOARD_URL
          const socialLoginRedirect = localStorage.getItem('socialLoginRedirect')
          localStorage.removeItem('socialLoginRedirect')
          const redirectUrl = socialLoginRedirect || loginRedirect.url || preferredAppUrl
          navigateToOnboarding(redirectUrl)
        }
        runAsync()
      }
    }
  }, [
    authenticatedUser,
    loginRedirect,
    navigateToOnboarding,
    handlePreferredAuthMethod,
    cookies,
    loginToAcademy,
    isAcademy,
    loginToDocs,
    isDocs,
    decodedState,
    searchParams,
  ])

  const checkWaitListMessage = useCallback(
    (message: string | null) => {
      if (message === WAIT_LIST_MESSAGE) {
        dispatch(clearAuth())
        navigateToOnboarding('/wait-list')
      } else if (message === LOGIN_WAIT_LIST_MESSAGE) {
        dispatch(clearAuth())
        navigate('/wait-list')
      }
    },
    [navigateToOnboarding, navigate, dispatch],
  )

  useEffect(() => {
    checkWaitListMessage(authMessage)
  }, [authMessage, checkWaitListMessage])

  const error = searchParams.get('error')
  const errorCode = searchParams.get('errorCode')
  const errorEmail = searchParams.get('email')
  const code = searchParams.get('code')

  // Handle redirect URL from search params
  useEffect(() => {
    const redirectUrl = searchParams.get('redirect')
    if (redirectUrl && isSafeRedirect(redirectUrl)) {
      dispatch(setLoginRedirectUrl(redirectUrl))
    }
  }, [dispatch, searchParams])

  // Handle error from URL params
  useEffect(() => {
    if (error) {
      dispatch(setAuthErrors([{ message: error, errorCode: errorCode ? Number(errorCode) : undefined, email: errorEmail ?? undefined }]))
    }
  }, [dispatch, error, errorCode, errorEmail])

  // Handle wait list message from URL params
  useEffect(() => {
    const message = searchParams.get('message')
    if (message) {
      checkWaitListMessage(message)
    }
  }, [searchParams, checkWaitListMessage])

  // Handle association code - only process once
  useEffect(() => {
    if (code && loginStatus === 'idle' && !authenticatedUser && !isRedirectingToAcademy.current && !isRedirectingToDocs.current) {
      dispatch(getTokenForAssociationCode({ code }))
    }
  }, [dispatch, code, loginStatus, authenticatedUser])

  if (code) {
    return <Redirect />
  }

  const toForgotPassword = () => setForgotPassword(true)
  const toLogin = () => setForgotPassword(false)

  if ((!authenticatedUser && readToken() != null) || authenticatedUser) {
    return <Redirect />
  }
  if (handlePreferredAuthMethodRedirect()) {
    return <Redirect />
  }

  const hasFullPageError = !!error || authErrors?.some((e) => e.errorCode != null && FULL_PAGE_ERROR_CODES.includes(e.errorCode))
  if (hasFullPageError) {
    return <LoginErrorPanel />
  }

  return (
    <div className='auth-container'>
      <SignupHeader />
      {forgotPassword ? <ForgotPassword toLogin={toLogin} /> : <Login toForgotPassword={toForgotPassword} />}
    </div>
  )
}

export default LoginViewV2

const Login: React.FC<{ toForgotPassword: () => void }> = ({ toForgotPassword }) => (
  <div className='auth-popup-container'>
    <div className='auth-popup-inner'>
      <h2 className='auth-popup-header'>Log in</h2>
      <LoginButtons toForgotPassword={toForgotPassword} />
    </div>
  </div>
)

const LoginButtons: React.FC<{ toForgotPassword: () => void }> = ({ toForgotPassword }) => {
  const { shouldUseCompanySso } = useCompanySso()
  const { executeRecaptcha } = useGoogleReCaptcha()

  type LoginValues = {
    email: string
    password: string
    stayLoggedIn: boolean
  }

  const dispatch = useAppDispatch()
  const authErrors = useAppSelector(selectAuthErrors)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    const message = authErrors
      ?.filter((error) => error.errorCode && [ERROR_INVITE_CODE_EXPIRED, ERROR_INVITE_CODE_EXPIRED_AND_RESENT].includes(error.errorCode))
      .find((error) => error.message)?.message
    if (message !== WAIT_LIST_MESSAGE) {
      setErrorMessage(message)
    }
  }, [authErrors])

  const form = useForm<LoginValues>({
    defaultValues: {
      email: '',
      password: '',
      stayLoggedIn: stayLoggedIn(),
    },
  })

  const { handleSubmit, control } = form

  const onSubmit = async (data: LoginValues) => {
    if (!executeRecaptcha) {
      return
    }

    const captchaToken = await executeRecaptcha('login')

    try {
      setLoading(true)
      const { email, password, stayLoggedIn } = data
      dispatch(loginWithUsernamePasswordV2({ email, password, stayLoggedIn, captchaToken })).finally(() => {
        setLoading(false)
      })
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <div className='auth-buttons'>
      <AuthErrorToast />
      <SocialButtons buttonText='Log in with ' flow='login' />
      {!shouldUseCompanySso() && (
        <>
          <AuthSeparator />
          <FormProvider {...form}>
            <form className='login-form-container' method='post' onSubmit={handleSubmit(onSubmit)}>
              <EmailInput loading={loading} />
              <div className='auth-input-field'>
                <label htmlFor='password' className='auth-form-label'>
                  Password
                </label>
                <Controller
                  name='password'
                  control={control}
                  render={({ field, fieldState }) => (
                    <Password
                      disabled={loading}
                      placeholder='Enter your password'
                      className='auth-password'
                      inputClassName={classNames('auth-input', { 'auth-input-error': fieldState.error })}
                      id={field.name}
                      {...field}
                      feedback={false}
                      toggleMask={true}
                      role='textbox'
                      aria-label='Password'
                      autoComplete='on'
                    />
                  )}
                />
                <CaptchaNote />
                <StayLoggedIn />
              </div>
              {errorMessage && <div className='p-error'>{errorMessage}</div>}
              <Button className='auth-email-button login-button' type='submit' loading={loading} disabled={!executeRecaptcha}>
                <img src={MailLogo} className='auth-button-logo' alt='Email logo' />
                <span className='auth-email-button-text'>Log in with Email</span>
              </Button>
              <div className='login-footer'>
                <button className='auth-link-button' onClick={toForgotPassword} type='button'>
                  <span className='auth-link-text'>Forgot your password?</span>
                </button>
              </div>
            </form>
          </FormProvider>
        </>
      )}
    </div>
  )
}

export const StayLoggedIn = () => {
  const { control } = useFormContext()
  return (
    <div className='auth-input-field pt-3'>
      <Controller
        name='stayLoggedIn'
        control={control}
        render={({ field }) => (
          <Form.Check
            id='staySignedInCheck'
            type='checkbox'
            checked={field.value === true}
            onChange={(e) => {
              setStayLoggedIn(e.currentTarget.checked)
              field.onChange(e)
            }}
            label='Stay signed in for a week'
          />
        )}
      />
    </div>
  )
}

const ForgotPassword: React.FC<{ toLogin: () => void }> = ({ toLogin }) => {
  const dispatch = useAppDispatch()
  const status = useAppSelector(selectForgotPasswordStatus)

  useEffect(() => {
    dispatch(clearAuthErrors())
    dispatch(clearForgotPasswordStatus())
  }, [dispatch])

  const form = useForm<{ email: string }>({
    defaultValues: {
      email: '',
    },
  })

  const { handleSubmit } = form

  const onSubmit = async (data: { email: string }) => {
    try {
      const { email } = data
      dispatch(forgotPasswordWithCode({ email }))
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <div className='forgot-password-container'>
      <button className='auth-link-button' onClick={toLogin} type='button'>
        <span className='icon-chevron-left auth-back-arrow' />
        <span className='auth-link-text'>Back</span>
      </button>
      <div className='auth-popup-container'>
        <div className='auth-popup-inner'>
          <h2 className='auth-popup-header'>Can't log in?</h2>
          <FormProvider {...form}>
            <form className='forgot-password-form-container' method='post' onSubmit={handleSubmit(onSubmit)}>
              <div className='forgot-password-text'>Type your email so we can send you a password recovery email</div>
              <EmailInput loading={status === 'loading'} />
              {status === 'succeeded' && (
                <div className='forgot-password-success-text'>
                  If the email is associated with an existing user, then we've sent you an email containing further instructions for resetting your password.
                </div>
              )}
              {status === 'succeeded' ? (
                <Button className='auth-email-button forgot-password-button-success' type='button'>
                  <span className='auth-email-button-text'>Success!</span>
                </Button>
              ) : (
                <Button className='auth-email-button' type='submit' loading={status === 'loading'}>
                  <span className='auth-email-button-text'>Reset password</span>
                </Button>
              )}

              <div className='auth-footer-text text-center'>
                Still having trouble? Contact us at <br />
                <a href='mailto:support@zocks.io' target='_blank' rel='noreferrer'>
                  support@zocks.io
                </a>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  )
}
